import { labels } from './labels';
import { navigate } from './navigate';

const signedOutListeners = new Set<() => void>();

/** `App` listens here: a `401` from any request means the session is gone, so it shows the login page. */
export function onSignedOut(listener: () => void): () => void {
  signedOutListeners.add(listener);
  return () => {
    signedOutListeners.delete(listener);
  };
}

/**
 * Every request in this file goes through here. The session is a cookie the browser sends on its
 * own (same origin, default `credentials`), so nothing is attached; a `401` (the session expired,
 * or was never there) tells `App` to show the login page (ADR 0029, ADR 0040).
 */
async function apiFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const response = await fetch(url, init);
  if (response.status === 401) signedOutListeners.forEach((listener) => listener());
  return response;
}

/**
 * The error a failed request throws. A `403` is the role guard (ADR 0040), so it reads the same on
 * every screen. For a write, any other status reads the backend's own `message` first, so the
 * operator sees why it was refused; `fallback` names the request when there is none.
 */
export class RoleDeniedError extends Error {
  constructor() {
    super(labels.roleDenied);
    this.name = 'RoleDeniedError';
  }
}

async function apiError(response: Response, fallback: string, isWrite = false): Promise<Error> {
  if (response.status === 403) return new RoleDeniedError();
  const body = isWrite ? await response.json().catch(() => null) : null;
  return new Error((body as { message?: string } | null)?.message ?? fallback);
}

export type ConsoleRole = 'viewer' | 'admin';

export interface ConsoleSession {
  username: string;
  role: ConsoleRole;
  /** SSO-22: a member of the group Super admins. It only shows the User admin link. */
  superAdmin: boolean;
  /** The user admin console of the realm. Only a super admin gets it. */
  userAdminUrl?: string;
}

/** The signed-in user, or `null` for no session. Not an `apiFetch` call: a `401` here is the answer. */
export async function fetchSession(): Promise<ConsoleSession | null> {
  const response = await fetch('/api/auth/me');
  return response.ok ? ((await response.json()) as ConsoleSession) : null;
}

/**
 * Ends the console session and sends the browser to the Keycloak end-session page, which ends the
 * Keycloak session too and returns to the console. Resolves `true` when it navigated. A failed
 * call leaves the person on the sign-in page: the next request settles whether the cookie is
 * still good.
 */
export async function logout(): Promise<boolean> {
  try {
    const response = await fetch('/api/auth/logout', { method: 'POST' });
    const { redirect } = (await response.json()) as { redirect?: unknown };
    if (typeof redirect !== 'string' || !redirect) return false;
    navigate(redirect);
    return true;
  } catch {
    return false;
  }
}

/**
 * The `GET /api/processing` response shape. **Duplicated from
 * `backend/src/processing/view.ts`**, not imported: frontend and backend are separate npm
 * packages with no shared build (the same reason every MBGE service duplicates its own
 * `artifact-store/` port rather than importing one). This is the service's own internal
 * contract, not an MBGE integration surface, so keeping the two in sync is this row's own
 * discipline rather than a locked decision — see this app's AGENTS.md.
 */
export interface ControlView {
  label: string;
  kind: 'guarded' | 'one-click' | 'link' | 'info';
  href?: string;
  disabled: boolean;
  note?: string;
  secondaryLink?: { label: string; href: string };
}

export interface ConditionRowView {
  id: string;
  headline: string;
  detail: string;
  control: ControlView;
  count?: number;
  isFloor?: boolean;
  /** `review-batch-stuck` only (QA-161): the sources that have a stuck batch. */
  stuckSources?: ('residual' | 'spot-check')[];
}

export interface DrainLineView {
  replacedByRow: boolean;
  count: number;
  isFloor: boolean;
  ageAgainstBound: string | null;
  control: ControlView;
}

export interface HistoricalImportView {
  runId: string;
  percentComplete: number;
  documentsPerDay: number;
  etaText: string | null;
  documentsCompleted: number;
  corpusTotal: number;
  isFloor: boolean;
}

export interface FirstPassCounts {
  waiting: number;
  inProgress: number;
  doneLastWeek: number;
  sentOn: number;
  notProcessed: number;
  gateVerdict: Record<string, number>;
  failureClass: Record<string, number>;
  doneLastWeekIsFloor?: boolean;
  waitingIsIncomplete?: boolean;
  inProgressIsIncomplete?: boolean;
}

export interface FirstPassTypeView extends FirstPassCounts {
  type: string;
  inProgressOldestAgeMs: number | null;
  inProgressIsFloor: boolean;
}

export interface FirstPassServiceView extends FirstPassCounts {
  service: string;
  waitingIsIncomplete?: boolean;
  inProgressIsIncomplete?: boolean;
}

export interface FirstPassStripView {
  windowStart: string;
  windowEnd: string;
  types: FirstPassTypeView[];
  services: FirstPassServiceView[];
}

export interface ProcessingView {
  checkedAt: string;
  documentsToday: { count: number; isFloor: boolean };
  workList: ConditionRowView[];
  drain: DrainLineView;
  spotCheckDrain: DrainLineView;
  historicalImport: HistoricalImportView | null;
  firstPassStrip?: FirstPassStripView;
}

export async function fetchProcessingView(): Promise<ProcessingView> {
  const response = await apiFetch('/api/processing');
  if (!response.ok) throw await apiError(response, `GET /api/processing -> ${response.status}`);
  return (await response.json()) as ProcessingView;
}

/** `7`, `30`, `90`, or `all` — the search screen's self-widening scan. */
export type SearchTier = '7' | '30' | '90' | 'all';

export type InFlightStage = 'first-pass' | 'human-review';

export interface InFlightAnswer {
  stage: InFlightStage;
  sinceISO: string;
  message: string;
}

export interface SearchResultRow {
  docId: string;
  type: string;
  caseKey?: string;
  pedimento?: string;
  cove?: string;
  invoice?: string;
  failureClass?: string;
}

export type SearchResponse =
  | { mode: 'empty' }
  | { mode: 'doc-id'; query: string; docId: string; found: true }
  | {
      mode: 'doc-id';
      query: string;
      docId: string;
      found: false;
      inFlight: InFlightAnswer | null;
      /** QA-157: no result, and a failure record puts the document on Not processed. */
      notProcessed?: { failureClass: FailureClass; ignored: boolean };
    }
  | { mode: 'join-key'; query: string; tier: SearchTier; daysSearched: number | null; results: SearchResultRow[] };

export async function fetchSearch(query: string, tier: SearchTier, signal?: AbortSignal): Promise<SearchResponse> {
  const params = new URLSearchParams({ q: query, tier });
  const response = await apiFetch(`/api/documents/search?${params.toString()}`, { signal });
  if (!response.ok) throw await apiError(response, `GET /api/documents/search -> ${response.status}`);
  return (await response.json()) as SearchResponse;
}

/** `GET /api/documents/browse` — duplicated from `backend/src/browse/view.ts` (QA-68). */
export interface BrowseRow {
  docId: string;
  type: string;
  /** The time of the latest activity, ISO, UTC. */
  at: string;
  tier?: string;
  gateVerdict?: string;
  reasonClass?: string[];
  pedimento?: string;
  cove?: string;
  invoice?: string;
}

export interface BrowseResponse {
  rows: BrowseRow[];
  nextCursor?: string;
}

export async function fetchBrowse(cursor: string | undefined, signal?: AbortSignal): Promise<BrowseResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  const response = await apiFetch(`/api/documents/browse?${params.toString()}`, { signal });
  if (!response.ok) throw await apiError(response, `GET /api/documents/browse -> ${response.status}`);
  return (await response.json()) as BrowseResponse;
}

// ---------------------------------------------------------------------------------------------
// #doc/<id> — duplicated from `backend/src/doc-record/view.ts`, the same house convention as
// every type above.
// ---------------------------------------------------------------------------------------------

export interface FieldView {
  name: string;
  value: unknown;
  source?: 'xml' | 'vlm' | 'reader' | 'human';
  confidenceLabel: string;
}

export interface CellView {
  source?: 'xml' | 'vlm' | 'reader' | 'human';
  confidenceLabel?: string;
  flagged: boolean;
}

export interface RepeatingGroupView {
  name: string;
  columns: string[];
  rows: Array<Record<string, unknown>>;
  /** One entry per row, keyed by column. */
  cells: Array<Record<string, CellView>>;
}

export interface GateOutcomeView {
  ruleId: string;
  status: string;
  reasonClass?: string;
  message: string;
  counterpartDocId?: string;
}

export interface GateReportView {
  available: boolean;
  ruleId?: string;
  reasonClass?: string;
  flaggedCells: string[];
  outcomes: GateOutcomeView[];
  minFieldConfidence: number | null;
  fieldsBelowThreshold: string[];
}

export interface DocRecordView {
  docId: string;
  type: string;
  layout?: string;
  xmlCoverage?: string;
  fields: FieldView[];
  repeatingGroups: RepeatingGroupView[];
  provenance: {
    skillVersion?: string;
    modelId?: string;
    gateVerdict?: string;
    tier?: string;
    attempt: number;
    inputRole?: string;
    parentStem: string;
    pageRange?: { firstPage: number; lastPage: number };
    reviewerIdentity?: string;
    reviewedAt?: string;
    readerNotes?: string[];
  };
  gateReport: GateReportView;
  langfuse: { traceId?: string; traceUrl?: string };
  extractionRulesHref: string;
  raw: { result: unknown; provenance: unknown };
  /** QA-150: who holds the document now. The page shows no Run again while this is set. */
  heldBy?: 'human-review' | 'random-check' | 'handoff';
}

/** QA-86: a document with no result that is on Not processed. Copy of `backend/src/doc-record/view.ts`. */
export interface DeadLetterDocView {
  docId: string;
  deadLetter: {
    failureClass: string;
    ignored: boolean;
    filedType: string;
    detectedType?: string;
    reason?: string;
    timestamp: string;
    attempt: number;
    acknowledgedBy?: string;
  };
}

export async function fetchDocRecord(docId: string): Promise<DocRecordView | DeadLetterDocView | null> {
  const response = await apiFetch(`/api/doc?id=${encodeURIComponent(docId)}`);
  if (response.status === 404) return null;
  if (!response.ok) throw await apiError(response, `GET /api/doc -> ${response.status}`);
  return (await response.json()) as DocRecordView | DeadLetterDocView;
}

/** QA-163: the URL of a document's stored source file (`GET /api/doc/file`), sent inline. */
export function sourceFileUrl(docId: string): string {
  return `/api/doc/file?id=${encodeURIComponent(docId)}`;
}

export type SourceFile =
  { status: 'ok'; blob: Blob } | { status: 'missing' } | { status: 'unsupported' } | { status: 'tooLarge' };

/** QA-163: the stored file, read only when the operator asks to see it. `404` is no stored file,
 *  `415` a stored file that is not a PDF or an image, and `413` a file over the console's size cap. */
export async function fetchSourceFile(docId: string, signal?: AbortSignal): Promise<SourceFile> {
  const url = sourceFileUrl(docId);
  const response = await apiFetch(url, { signal });
  if (response.status === 404) return { status: 'missing' };
  if (response.status === 415) return { status: 'unsupported' };
  if (response.status === 413) return { status: 'tooLarge' };
  if (!response.ok) throw await apiError(response, `GET ${url} -> ${response.status}`);
  return { status: 'ok', blob: await response.blob() };
}

// ---------------------------------------------------------------------------------------------
// #not-processed — duplicated from `backend/src/dead-letter/view.ts`.
// ---------------------------------------------------------------------------------------------

export type FailureClass = 'poison' | 'misfiled' | 'unroutable' | 'collision' | 'internal';

export interface FailureEntryView {
  docId: string;
  type: string;
  filedType: string;
  detectedType?: string;
  ruleId?: string;
  error?: string;
  timestamp: string;
  attempt: number;
  claimedKey?: string;
  arrivingKey?: string;
  /** `internal` only (BUG-01): which pipeline stage threw. */
  step?: string;
  /** `internal` only (BUG-01): the caught error's `.name`, never `.message`. */
  errorClass?: string;
  retrying?: boolean;
  /** QA-158: who holds the document while it has a completed result (*Try again* waits). */
  heldBy?: 'human-review' | 'random-check' | 'handoff';
}

/** QA-85: the heading of each group. The rows come from `fetchFailurePage`, and the copy text from
 *  `fetchCopyText`. */
export interface FailureGroupSummary {
  failureClass: FailureClass;
  count: number;
  isFloor: boolean;
}

export interface IgnoredEntryView extends FailureEntryView {
  failureClass: FailureClass;
  acknowledgedBy: string;
  acknowledgedAt: string;
}

export interface IgnoredSummary {
  count: number;
  isFloor: boolean;
}

export interface NotProcessedSummary {
  groups: FailureGroupSummary[];
  ignored: IgnoredSummary;
}

export interface FailurePage {
  entries: FailureEntryView[];
  nextCursor?: string;
}

export interface IgnoredPage {
  entries: IgnoredEntryView[];
  nextCursor?: string;
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await apiFetch(url, { signal });
  if (!response.ok) throw await apiError(response, `GET ${url} -> ${response.status}`);
  return (await response.json()) as T;
}

export function fetchNotProcessed(): Promise<NotProcessedSummary> {
  return getJson('/api/not-processed');
}

const pageUrl = (path: string, cursor: string | undefined) =>
  cursor ? `${path}?cursor=${encodeURIComponent(cursor)}` : path;

export function fetchFailurePage(
  failureClass: FailureClass,
  cursor: string | undefined,
  signal?: AbortSignal,
): Promise<FailurePage> {
  return getJson(pageUrl(`/api/not-processed/${failureClass}`, cursor), signal);
}

export function fetchIgnoredPage(cursor: string | undefined, signal?: AbortSignal): Promise<IgnoredPage> {
  return getJson(pageUrl('/api/not-processed/ignored', cursor), signal);
}

/** **Copy the list**: the text of the whole group, read when the operator asks. */
export async function fetchCopyText(failureClass: FailureClass): Promise<string> {
  return (await getJson<{ copyText: string }>(`/api/not-processed/${failureClass}/copy-text`)).copyText;
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw await apiError(response, `POST ${url} -> ${response.status}`, true);
  }
  return (await response.json()) as T;
}

export function tryAgain(docIds: string[]): Promise<{ attempt: number }[]> {
  return postJson('/api/not-processed/try-again', { docIds });
}

/** QA-70: *Run again* on the document page (QA-83). `attempt` is the new attempt number. */
export function runDocumentAgain(docId: string): Promise<{ docId: string; attempt: number }> {
  return postJson('/api/documents/run-again', { docId });
}

export function acknowledgeDocuments(docIds: string[]): Promise<{ acknowledged: number }> {
  return postJson('/api/not-processed/acknowledge', { docIds });
}

export function unignoreDocuments(docIds: string[]): Promise<{ unignored: number }> {
  return postJson('/api/not-processed/unignore', { docIds });
}

// ---------------------------------------------------------------------------------------------
// #settings, API clients (ADR 0043) — duplicated from `backend/src/oauth/api-clients.service.ts`.
// ---------------------------------------------------------------------------------------------

export interface OauthClientView {
  /** The Keycloak client ID, `mbge_<random>`. */
  id: string;
  name: string;
  createdAt: string | null;
  createdBy: string | null;
  /** False after an admin disables the client, in the card or in the user admin console. */
  enabled: boolean;
  /** The minutes a token of this client lasts, or `null` when the console cannot read it. */
  tokenMinutes: number | null;
  /** False when the client has no `api-client` marker mapper (ADR 0044): every token gets 401. */
  apiAccess: boolean;
}

export interface CreatedOauthClient {
  id: string;
  name: string;
  /** Returned by the create call only; Keycloak keeps it. */
  secret: string;
}

export interface OauthClientList {
  clients: OauthClientView[];
  /** Where a caller gets its token: `<OIDC_ISSUER_URL>/protocol/openid-connect/token`. */
  tokenUrl: string;
}

export async function fetchOauthClients(): Promise<OauthClientList> {
  const response = await apiFetch('/api/oauth/clients');
  if (!response.ok) throw await apiError(response, `GET /api/oauth/clients -> ${response.status}`);
  return (await response.json()) as OauthClientList;
}

export function createOauthClient(name: string): Promise<CreatedOauthClient> {
  return postJson('/api/oauth/clients', { name });
}

/** A new secret. Like the create call, it returns the secret this one time. */
export function newOauthClientSecret(id: string): Promise<CreatedOauthClient> {
  return postJson(`/api/oauth/clients/${encodeURIComponent(id)}/secret`, {});
}

/** Enable or disable a client, or set the minutes a token of it lasts (1 to 1440). */
export async function updateOauthClient(id: string, patch: { enabled?: boolean; tokenMinutes?: number }): Promise<void> {
  const response = await apiFetch(`/api/oauth/clients/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!response.ok) {
    throw await apiError(response, `PATCH /api/oauth/clients -> ${response.status}`, true);
  }
}

export async function revokeOauthClient(id: string): Promise<void> {
  const response = await apiFetch(`/api/oauth/clients/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!response.ok) {
    throw await apiError(response, `DELETE /api/oauth/clients -> ${response.status}`, true);
  }
}

// ---------------------------------------------------------------------------------------------
// #extraction-rules — duplicated from `backend/src/extraction-rules/view.ts`.
// ---------------------------------------------------------------------------------------------

export interface VersionListEntryView {
  version: string;
  publishedAt: string;
  active: boolean;
}

export interface WaitingVersionView {
  version: string;
  since: string;
  changeList: string[];
}

export interface BackfillWarningView {
  runId: string;
  documentsInFlightOnCurrentVersion: number;
  inFlightIsFloor: boolean;
  documentsLeftInRun: number;
}

export interface ExtractionRulesTypeView {
  type: string;
  activeVersion: string | null;
  mode: 'auto' | 'pinned';
  changedAt: string | null;
  changedBy: string | null;
  versions: VersionListEntryView[];
  waitingVersion: WaitingVersionView | null;
  backfillWarning: BackfillWarningView | null;
}

export async function fetchExtractionRules(): Promise<ExtractionRulesTypeView[]> {
  const response = await apiFetch('/api/extraction-rules');
  if (!response.ok) throw await apiError(response, `GET /api/extraction-rules -> ${response.status}`);
  return (await response.json()) as ExtractionRulesTypeView[];
}

export function pinVersion(type: string, version: string, reason: string) {
  return postJson(`/api/extraction-rules/${type}/pin`, { version, reason });
}

export function unpinVersion(type: string, reason: string, to?: string) {
  return postJson(`/api/extraction-rules/${type}/unpin`, { reason, to });
}

// ---------------------------------------------------------------------------------------------
// #processing/why
// ---------------------------------------------------------------------------------------------

export interface WhyDocumentView {
  docId: string;
  type: string;
  layout: string | null;
  reasonClass: string | null;
}

export interface ReasonClassCountView {
  reasonClass: string;
  countByType: Record<string, number>;
  total: number;
}

export interface WhyView {
  breakdown: ReasonClassCountView[];
  unrecordedCount: number;
  documents: WhyDocumentView[];
  isFloor: boolean;
}

export async function fetchWhy(): Promise<WhyView> {
  const response = await apiFetch('/api/processing/why');
  if (!response.ok) throw await apiError(response, `GET /api/processing/why -> ${response.status}`);
  return (await response.json()) as WhyView;
}

// ---------------------------------------------------------------------------------------------
// Pause everything / Resume everything — duplicated from `backend/src/pause-everything/view.ts`
// (ADR 0018, OPS-04).
// ---------------------------------------------------------------------------------------------

export interface PauseEverythingView {
  paused: boolean;
  pausedAt: string | null;
  pausedBy: string | null;
  pausedReason: string | null;
  cause: 'operator' | 'unreadable' | null;
  inboxCount: number;
  inboxIsFloor: boolean;
}

export async function fetchPauseEverything(): Promise<PauseEverythingView> {
  const response = await apiFetch('/api/processing/pause-everything');
  if (!response.ok) throw await apiError(response, `GET /api/processing/pause-everything -> ${response.status}`);
  return (await response.json()) as PauseEverythingView;
}

export function pauseEverything(reason?: string): Promise<PauseEverythingView> {
  return postJson('/api/processing/pause-everything/pause', { reason });
}

export function resumeEverything(reason?: string): Promise<PauseEverythingView> {
  return postJson('/api/processing/pause-everything/resume', { reason });
}

// ---------------------------------------------------------------------------------------------
// #settings — duplicated from `backend/src/settings/view.ts` (OPS-04).
// ---------------------------------------------------------------------------------------------

export interface SettingsBoundsView {
  humanReviewMs: number | null;
  deadLetterMs: number | null;
  spotCheckMs: number | null;
  stalledMs: number | null;
  deadLetterBurstCount: number | null;
  deadLetterBurstWindowMs: number | null;
}

export interface SettingsView {
  bounds: SettingsBoundsView;
  recipients: string[];
  pauseGraceMs: number | null;
}

export interface ChangeEntry {
  field: string;
  from: unknown;
  to: unknown;
}

export interface SaveSettingsResult {
  view: SettingsView;
  changes: ChangeEntry[];
}

export async function fetchSettings(): Promise<SettingsView> {
  const response = await apiFetch('/api/settings');
  if (!response.ok) throw await apiError(response, `GET /api/settings -> ${response.status}`);
  return (await response.json()) as SettingsView;
}

export function saveSettings(
  input: {
    humanReviewMs: number | null;
    deadLetterMs: number | null;
    spotCheckMs: number | null;
    stalledMs: number | null;
    deadLetterBurstCount: number | null;
    deadLetterBurstWindowMs: number | null;
    recipients: string[];
    pauseGraceMs: number | null;
  },
): Promise<SaveSettingsResult> {
  return postJson('/api/settings/save', input);
}

// ---------------------------------------------------------------------------------------------
// Random checks — duplicated from `backend/src/extraction-rules/view.ts` (ADR 0019, OPS-04).
// ---------------------------------------------------------------------------------------------

export interface RandomChecksTypeView {
  type: string;
  targetPerWeek: number | null;
  checkedLastWeek: number;
  ofArrivalsPercent: number | null;
  accuracyPercent: number | null;
  accuracySample: number;
}

export async function fetchRandomChecks(): Promise<RandomChecksTypeView[]> {
  const response = await apiFetch('/api/extraction-rules/random-checks');
  if (!response.ok) throw await apiError(response, `GET /api/extraction-rules/random-checks -> ${response.status}`);
  return (await response.json()) as RandomChecksTypeView[];
}

export function changeRandomCheckTarget(type: string, targetPerWeek: number): Promise<RandomChecksTypeView> {
  return postJson(`/api/extraction-rules/random-checks/${type}`, { targetPerWeek });
}

// ---------------------------------------------------------------------------------------------
// Send to review — duplicated from `backend/src/review/view.ts` (OPS-04).
// ---------------------------------------------------------------------------------------------

export type ReviewSource = 'escalation' | 'spot-check';

export interface ReviewCandidateView {
  docId: string;
  type: string;
  /** QA-149: why the last send of this type did not send the document, and when. */
  notSent?: { reason: string; at: string };
}

export interface ReviewCandidatesView {
  source: ReviewSource;
  candidates: ReviewCandidateView[];
  isFloor: boolean;
}

export interface SendToReviewGroupResult {
  type: string;
  source: ReviewSource;
  batchId: string | null;
  documentCount: number;
  /** The batch manifest's status when escalation answered (ESC-20: it answers once staged). */
  status?: string;
  projectId?: string;
  /** QA-13: selected stems that did not go, with the reason escalation gave. */
  skipped?: { stem: string; reason: string; detail: string }[];
  error?: string;
}

export type StuckReason = 'failed' | 'not-delivered' | 'stems-pending';

export interface ReviewBatchView {
  type: string;
  source: ReviewSource;
  batchId: string;
  documentCount: number;
  status: string;
  projectId?: string;
  createdAt: string;
  updatedAt: string;
  ageMs: number;
  stuck: boolean;
  stuckReason?: StuckReason;
}

/** QA-120: when escalation's results poller last finished a run. */
export type ReviewPollerState = 'ok' | 'stale' | 'unfinished' | 'starting' | 'off' | 'never';

export interface ReviewPollerView {
  state: ReviewPollerState;
  startedAt?: string;
  finishedAt?: string;
}

/** QA-132: whether a send to review can work, for each source. `off` means Data Studio is not set up. */
export interface ReviewSendView {
  review: 'ready' | 'off';
  randomCheck: 'ready' | 'off';
}

export interface ReviewBatchesView {
  batches: ReviewBatchView[];
  stuckAfterMs: number;
  isFloor: boolean;
  /** Absent in an answer from a backend that does not report it: the panel shows no line. */
  poller?: ReviewPollerView;
  /** Absent in an answer from an older backend: treated as ready. */
  send?: ReviewSendView;
}

export interface SendToReviewResult {
  results: SendToReviewGroupResult[];
}

export async function fetchReviewCandidates(source: ReviewSource): Promise<ReviewCandidatesView> {
  const response = await apiFetch(`/api/processing/review/candidates?source=${source}`);
  if (!response.ok) throw await apiError(response, `GET /api/processing/review/candidates -> ${response.status}`);
  return (await response.json()) as ReviewCandidatesView;
}

/** ESC-20: the batch manifests, with the project id once the job delivered, and the stuck ones. */
export async function fetchReviewBatches(): Promise<ReviewBatchesView> {
  const response = await apiFetch('/api/processing/review/batches');
  if (!response.ok) throw await apiError(response, `GET /api/processing/review/batches -> ${response.status}`);
  return (await response.json()) as ReviewBatchesView;
}

export function sendToReview(
  docIds: string[],
  source: ReviewSource,
  reason: string,
): Promise<SendToReviewResult> {
  return postJson('/api/processing/review/send', { docIds, source, reason });
}

// ---------------------------------------------------------------------------------------------
// #test-extraction — duplicated from `backend/src/test-extraction/view.ts` (QA-34).
// ---------------------------------------------------------------------------------------------

export type TestRunState = 'waiting' | 'in-progress' | 'completed' | 'not-processed' | 'ignored';

export interface TestRunView {
  docId: string;
  at: string;
  by: string;
  files: string[];
  state: TestRunState;
  paused?: boolean;
  verdict?: string;
  reason?: string;
  failureClass?: string;
  detectedType?: string;
}

/** Copy of `TestExtractionListView` in `backend/src/test-extraction/view.ts`. */
export interface TestRunsResponse {
  runs: TestRunView[];
  /** QA-69: absent on the last page. */
  nextCursor?: string;
}

export async function fetchTestRuns(cursor?: string, signal?: AbortSignal): Promise<TestRunsResponse> {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  const response = await apiFetch(`/api/test-extraction${query}`, { signal });
  if (!response.ok) throw await apiError(response, `GET /api/test-extraction -> ${response.status}`);
  return (await response.json()) as TestRunsResponse;
}

/** QA-67: upload the stored files of one run again, with a type the operator picks. */
export function runTestAgain(docId: string, type: string): Promise<{ docId: string }> {
  return postJson('/api/test-extraction/run-again', { docId, type });
}

/** QA-67: hide one run from the list. Nothing else changes. */
export function removeTestRun(docId: string): Promise<{ ok: true }> {
  return postJson('/api/test-extraction/remove', { docId });
}

/** The browser sets the multipart boundary itself, so no `Content-Type` is passed. */
export async function uploadTestExtraction(type: string, files: File[]): Promise<{ docId: string }> {
  const body = new FormData();
  body.append('type', type);
  for (const file of files) body.append('files', file, file.name);
  const response = await apiFetch('/api/test-extraction', { method: 'POST', body });
  if (!response.ok) {
    throw await apiError(response, `POST /api/test-extraction -> ${response.status}`, true);
  }
  return (await response.json()) as { docId: string };
}
